import { gql } from '@apollo/client';
import Avatar, { type AvatarShape, type IAvatarProps } from '@orn/shared-web/components/Avatar';
import { getInitial } from '@orn/shared-web/utils/getInitials';

import type { EntityAvatarFragment } from './__generated__/EntityAvatar.gql';

interface IEntityAvatarProps extends Omit<IAvatarProps, 'src' | 'contrastBorder' | 'initials' | 'shape'> {
  entity: EntityAvatarFragment;
}

const EntityAvatar = (props: IEntityAvatarProps) => {
  const { entity, ...avatarProps } = props;
  const { src, shape, contrastBorder, initial } = getAvartarProps(entity);

  return <Avatar {...avatarProps} src={src} shape={shape} contrastBorder={contrastBorder} initials={initial} />;
};

const EntityAvatarFragment = gql`
  fragment EntityAvatar on IEntity {
    id
    name

    ... on User {
      avatarUrl
    }
    ... on Bank {
      logoBase64Encoding
      logoUrl
    }
    ... on Merchant {
      logoUrl
    }

    ... on FinancialAccount {
      bank {
        id
        logoBase64Encoding
        logoUrl
      }
    }
  }
`;

export default Object.assign(EntityAvatar, { Fragment: EntityAvatarFragment });

const getAvartarProps = (entity: EntityAvatarFragment) => {
  const type = entity.__typename;
  const initial = getInitial(entity.name);

  const bank = type === 'FinancialAccount' ? entity.bank : null;

  switch (type) {
    case 'User':
      return {
        src: entity.avatarUrl,
        shape: 'circle' as AvatarShape,
        contrastBorder: !entity.avatarUrl,
        initial,
      };
    case 'FinancialAccount':
      return {
        src: bank?.logoUrl
          ? bank?.logoUrl
          : bank?.logoBase64Encoding
            ? `data:image/png;base64,${bank?.logoBase64Encoding}`
            : null,
        shape: 'square' as AvatarShape,
        contrastBorder: !bank?.logoUrl && !bank?.logoBase64Encoding,
        initial,
      };
    case 'Bank':
      return {
        src: entity.logoUrl
          ? entity.logoUrl
          : entity.logoBase64Encoding
            ? `data:image/png;base64,${entity.logoBase64Encoding}`
            : null,
        shape: 'square' as AvatarShape,
        contrastBorder: !entity.logoUrl && !entity.logoBase64Encoding,
        initial,
      };
    case 'Merchant':
      return {
        src: entity.logoUrl,
        shape: 'circle' as AvatarShape,
        contrastBorder: !entity.logoUrl,
        initial,
      };
    default:
      return { src: null, shape: 'circle' as AvatarShape, contrastBorder: true, initial };
  }
};
